import { useAuth, useUser } from "@clerk/react-router";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { queryKeys } from "../api";
import {
  setApiRequestsBlocked,
  setAuthTokenGetter,
  setUnauthorizedSessionHandler,
} from "../api/client";
import {
  isPanelistNotFoundError,
  subscribeToUnauthorized,
} from "../api/errors";
import { signOut as signOutApi } from "../api/fetchers";
import { usePanelId, usePanelist, useSessionContext } from "../api/hooks";
import { setFlexSessionId } from "../api/session";
import { usePanelPath } from "../routing";
import { log } from "../utils/logging";

export function getPrimaryEmail(user: ReturnType<typeof useUser>["user"]) {
  return (
    user?.primaryEmailAddress?.emailAddress ??
    user?.emailAddresses[0]?.emailAddress
  );
}

export function usePortalAuthGate() {
  const { getToken, isLoaded, isSignedIn, signOut, sessionId } = useAuth();
  const [contextReady, setContextReady] = useState(false);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [isResettingSession, setIsResettingSession] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const panelId = usePanelId();
  const panelPath = usePanelPath();
  const currentEmail = getPrimaryEmail(user);
  const currentId = user?.id;

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      setApiRequestsBlocked(false);
      setAuthTokenGetter(null);
      setFlexSessionId(null);
      queryClient.clear();
      return;
    }

    setApiRequestsBlocked(false);
    setAuthTokenGetter(async (skipCache) => {
      const token = await getToken({ skipCache });
      tokenRef.current = token;
      return token;
    });

    return () => setAuthTokenGetter(null);
  }, [getToken, isLoaded, isSignedIn, queryClient]);

  useEffect(() => {
    const unsubscribe = subscribeToUnauthorized(() => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionContext(panelId),
      });
    });

    return unsubscribe;
  }, [panelId, queryClient]);

  const sessionContextQuery = useSessionContext(
    { panelId },
    {
      enabled: isLoaded && isSignedIn && Boolean(sessionId),
    },
  );

  console.log("sessionContextQuery", sessionContextQuery.data);

  useEffect(() => {
    if (!sessionContextQuery.data?.sessionId) {
      setContextReady(false);
      return;
    }

    setFlexSessionId(sessionContextQuery.data.sessionId);
    setContextReady(true);
  }, [sessionContextQuery.data?.sessionId]);

  const panelistQueryEnabled =
    isLoaded && isSignedIn && Boolean(sessionId) && contextReady;
  const panelistQuery = usePanelist({
    enabled: panelistQueryEnabled,
  });

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: getPrimaryEmail(user),
        username: user.username ?? user.fullName ?? undefined,
        panelId: panelId ?? undefined,
        panelMemberId: panelistQuery.data?.id ?? undefined,
      });
    }
  }, [user, panelistQuery.data]);

  const isMissingPanelist =
    isSignedIn &&
    (isPanelistNotFoundError(panelistQuery.error) ||
      isPanelistNotFoundError(sessionContextQuery.error));
  const hasLoginError =
    isSignedIn && panelistQuery.isError && !isMissingPanelist;

  useEffect(() => {
    setApiRequestsBlocked(Boolean(isSignedIn && hasLoginError));
  }, [hasLoginError, isSignedIn]);

  const resetSessionAndReturnHome = useCallback(async () => {
    if (isResettingSession) {
      return;
    }

    setIsResettingSession(true);
    setApiRequestsBlocked(true);

    const pathToHome = panelPath("/");

    try {
      try {
        await signOutApi();
      } catch {
        // Best-effort backend logout.
      }

      setAuthTokenGetter(null);
      setFlexSessionId(null);
      queryClient.clear();
      await signOut({ redirectUrl: pathToHome });
    } finally {
      setIsResettingSession(false);
    }
  }, [isResettingSession, panelPath, queryClient, signOut]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      setUnauthorizedSessionHandler(null);
      return;
    }

    setUnauthorizedSessionHandler(() => {
      log.warn("Unauthorized session detected. Resetting session.");
      queryClient.resetQueries({ queryKey: queryKeys.sessionContext(panelId) });
    });
    return () => setUnauthorizedSessionHandler(null);
  }, [isLoaded, isSignedIn, resetSessionAndReturnHome]);

  const isLoadingAccount =
    !isLoaded ||
    (isSignedIn && (sessionContextQuery.isLoading || panelistQuery.isLoading));

  return {
    currentEmail,
    currentId,
    hasLoginError,
    isLoaded,
    isLoadingAccount,
    isMissingPanelist,
    isResettingSession,
    isSignedIn,
    panelId,
    panelistQuery,
    resetSessionAndReturnHome,
    sessionContextQuery,
  };
}
